import { Module } from 'vuex';
import {
    ConstructionActionContext,
    ConstructionAdvisor,
    ConstructionContact,
    ConstructionDuration,
    ConstructionLocation,
    ConstructionMatrix,
    ConstructionRisks,
    ConstructionStoreActions,
    ConstructionStoreGetters,
    ConstructionStoreState,
    ConstructionSum,
    ConstructionTypes,
} from './ConstructionTypes';
import {
    ConstructionProjectEnum,
    ConstructionTypeEnum,
    OfferingLevelEnum,
} from '../domain/webservice-dto';
import { Successful } from './utils/action.utils';
import { initialConstructionStore } from './ConstructionInitialStore';
import { encodeUrl, getBaseUrl } from '../utils/url-encode-decode';
import { Construction } from '@/app/domain/constructionStoreInterface';
import { CipCodeSprache } from '@/app/generated/dto/dto-typings';
import { newDate } from '@/app/utils/date-util';

export const ConstructionStoreModule: Module<ConstructionStoreState, any> = {
// -------------------------------------------------------------------------
// Define Initial state
// -------------------------------------------------------------------------
    state: {
        ...initialConstructionStore,
    },
// -------------------------------------------------------------------------
// Define getters
// -------------------------------------------------------------------------
    getters: {
        [ConstructionStoreGetters.ConstructionFormIsDirty](state: ConstructionStoreState): boolean {
            return state.isDirty;
        },
        [ConstructionStoreGetters.ConstructionShareLink](state: ConstructionStoreState): string {
            return state.shareLink;
        },
        [ConstructionStoreGetters.ConstructionAdvisor](state: ConstructionStoreState): ConstructionAdvisor {
            return {
                advisorText: state.data.advisorText,
                advisorMail: state.data.advisorMail,
            };
        },
        [ConstructionStoreGetters.ConstructionFormIsSubmitting](state: ConstructionStoreState): boolean {
            return state.isSubmitting;
        },
        [ConstructionStoreGetters.ConstructionTypes](state: ConstructionStoreState): ConstructionTypes {
            return {
                constructionProject: state.data.constructionProject,
                constructionType: state.data.constructionType,
            };
        },
        [ConstructionStoreGetters.ConstructionProject](state: ConstructionStoreState): ConstructionProjectEnum | undefined {
            return state.data.constructionProject;
        },
        [ConstructionStoreGetters.ConstructionType](state: ConstructionStoreState): ConstructionTypeEnum | undefined {
            return state.data.constructionType;
        },
        [ConstructionStoreGetters.ConstructionLocation](state: ConstructionStoreState): ConstructionLocation {
            return {
                postcode: state.data.postcode,
                city: state.data.city,
                canton: state.data.canton,
                street: state.data.street,
            };
        },
        [ConstructionStoreGetters.ConstructionRisks](state: ConstructionStoreState): ConstructionRisks {
            return {
                newConstruction: state.data.newConstruction,
                modificationLoadBearingParts: state.data.supportStructureChanged,
                constructionInDangerZone: state.data.dangerZone,
                pileDrivingElements: state.data.ram,
                loweringGroundWater: state.data.groundwater,
                undertakingBuilding: state.data.underpinning,
                buildingOnRock: state.data.rock,
            };
        },
        [ConstructionStoreGetters.ConstructionSum](state: ConstructionStoreState): ConstructionSum {
            return {
                constructionSum: state.data.constructionSum as number,
            };
        },
        [ConstructionStoreGetters.ConstructionDuration](state: ConstructionStoreState): ConstructionDuration {
            return {
                duration: state.data.duration,
                startDate: state.data.startDate,
                completionDate: state.data.completionDate,
            };
        },
        [ConstructionStoreGetters.ConstructionMatrix](state: ConstructionStoreState): ConstructionMatrix {
            if (state.data.insurance) {
                return {
                    offeringLevel: state.data.insurance.offeringLevel,
                };
            } else {
                return {
                    offeringLevel: OfferingLevelEnum.Medium,
                };
            }
        },
        [ConstructionStoreGetters.ConstructionContact](state: ConstructionStoreState): ConstructionContact {
            if (state.data.principalLiability) {
                return {
                    salutation: state.data.principalLiability.salutation,
                    postcode: state.data.principalLiability.postcode,
                    city: state.data.principalLiability.city,
                    canton: state.data.principalLiability.canton,
                    street: state.data.principalLiability.street,
                    firstName: state.data.principalLiability.firstName,
                    lastName: state.data.principalLiability.lastName,
                    dateOfBirth: state.data.principalLiability.dateOfBirth,
                    email: state.data.principalLiability.email,
                    phone: state.data.principalLiability.phoneNumber,
                    companyName: state.data.principalLiability.companyName,
                    languageOfCorrespondence: state.data.principalLiability.languageOfCorrespondence,
                };
            } else {
                return {
                    ...state.data,
                };
            }
        },
        [ConstructionStoreGetters.QuoteId](state: ConstructionStoreState): string | undefined {
            return state.data.quoteId;
        },
        [ConstructionStoreGetters.Construction](state: ConstructionStoreState): Construction {
            return state.data;
        },
    },
// -------------------------------------------------------------------------
// Define actions
// -------------------------------------------------------------------------
    actions: {
        [ConstructionStoreActions.ResetStore](
            { commit, dispatch }: ConstructionActionContext): void {
            commit(Successful(ConstructionStoreActions.ResetStore), initialConstructionStore);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionFormIsSubmitting](
            { commit, state, dispatch }: ConstructionActionContext, isSubmitting?: boolean): void {
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsSubmitting), isSubmitting);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionType](
            { commit, state, dispatch }: ConstructionActionContext, constructionType?: ConstructionTypeEnum): void {
            commit(Successful(ConstructionStoreActions.SaveConstructionType), constructionType);
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsDirty), true);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionLocation](
            { commit, state, dispatch }: ConstructionActionContext, constructionLocation: ConstructionLocation): void {
            commit(Successful(ConstructionStoreActions.SaveConstructionLocation), constructionLocation);
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsDirty), true);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionRisks](
            { commit, state, dispatch }: ConstructionActionContext, constructionRisks: ConstructionRisks): void {
            commit(Successful(ConstructionStoreActions.SaveConstructionRisks), constructionRisks);
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsDirty), true);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionDuration](
            { commit, state, dispatch }: ConstructionActionContext, constructionDuration: ConstructionDuration): void {
            commit(Successful(ConstructionStoreActions.SaveConstructionDuration), constructionDuration);
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsDirty), true);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionSum](
            { commit, state, dispatch }: ConstructionActionContext, constructionSum: ConstructionSum): void {
            commit(Successful(ConstructionStoreActions.SaveConstructionSum), constructionSum);
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsDirty), true);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionMatrix](
            { commit, state, dispatch }: ConstructionActionContext, constructionMatrix: ConstructionMatrix): void {
            commit(Successful(ConstructionStoreActions.SaveConstructionMatrix), constructionMatrix);
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsDirty), true);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionContact](
            { commit, state, dispatch }: ConstructionActionContext, constructionContact: ConstructionContact): void {
            commit(Successful(ConstructionStoreActions.SaveConstructionContact), constructionContact);
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsDirty), true);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionLanguage](
            { commit, state, dispatch }: ConstructionActionContext, language: CipCodeSprache): void {
            commit(Successful(ConstructionStoreActions.SaveConstructionLanguage), language);
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsDirty), true);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionAdvisor](
            { commit, state, dispatch }: ConstructionActionContext, advisor: ConstructionAdvisor): void {
            commit(Successful(ConstructionStoreActions.SaveConstructionAdvisor), advisor);
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsDirty), true);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstruction](
            { commit, state, dispatch }: ConstructionActionContext, construction: Construction): void {
            commit(Successful(ConstructionStoreActions.SaveConstruction), construction);
            commit(Successful(ConstructionStoreActions.SaveConstructionFormIsDirty), true);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveQuoteId](
            { commit, state, dispatch }: ConstructionActionContext, quoteId: string): void {
            commit(Successful(ConstructionStoreActions.SaveQuoteId), quoteId);
            dispatch(ConstructionStoreActions.SaveConstructionOverviewLink);
            dispatch(ConstructionStoreActions.SaveConstructionShareLink);
        },
        [ConstructionStoreActions.SaveConstructionOverviewLink](
            { commit, state }: ConstructionActionContext): void {
            const overviewLink = `${getBaseUrl()}/overview?data=${encodeUrl({
                ...state.data,
                overviewLink: '',
            })}`;
            commit(Successful(ConstructionStoreActions.SaveConstructionOverviewLink), overviewLink);
        },
        [ConstructionStoreActions.SaveConstructionShareLink](
            { commit, state }: ConstructionActionContext): void {
            const shareLink = `${getBaseUrl()}/summary?data=${encodeUrl({
                ...state.data,
                overviewLink: '',
            })}`;
            commit(Successful(ConstructionStoreActions.SaveConstructionShareLink), shareLink);
        },
    },
// -------------------------------------------------------------------------
// Define mutations
// -------------------------------------------------------------------------
    mutations: {
        [Successful(ConstructionStoreActions.SaveConstructionOverviewLink)](
            state: ConstructionStoreState, overviewLink: string): void {
            state.data.overviewLink = overviewLink;
        },
        [Successful(ConstructionStoreActions.SaveConstructionShareLink)](
            state: ConstructionStoreState, shareLink: string): void {
            state.shareLink = shareLink;
        },
        [Successful(ConstructionStoreActions.SaveConstructionAdvisor)](
            state: ConstructionStoreState, advisor: ConstructionAdvisor): void {
            state.data.advisorText = advisor.advisorText;
            state.data.advisorMail = advisor.advisorMail;
        },
        [Successful(ConstructionStoreActions.ResetStore)](
            state: ConstructionStoreState, initialStore: ConstructionStoreState): void {
            state.isDirty = false;
            state.isSubmitting = false;
            state.data = initialStore.data;
        },
        [Successful(ConstructionStoreActions.SaveConstructionFormIsDirty)](
            state: ConstructionStoreState, isDirty: boolean): void {
            state.isDirty = isDirty;
        },
        [Successful(ConstructionStoreActions.SaveConstructionFormIsSubmitting)](
            state: ConstructionStoreState, isSubmitting: boolean): void {
            state.isSubmitting = isSubmitting;
        },
        [Successful(ConstructionStoreActions.SaveConstructionType)](
            state: ConstructionStoreState, constructionType?: ConstructionTypeEnum): void {
            state.data.constructionType = constructionType;
        },
        [Successful(ConstructionStoreActions.SaveConstructionLocation)](
            state: ConstructionStoreState, constructionLocation: ConstructionLocation): void {
            state.data.postcode = constructionLocation.postcode;
            state.data.city = constructionLocation.city;
            state.data.canton = constructionLocation.canton;
            state.data.street = constructionLocation.street;
        },
        [Successful(ConstructionStoreActions.SaveConstructionRisks)](
            state: ConstructionStoreState, constructionRisks: ConstructionRisks): void {
            state.data.newConstruction = constructionRisks.newConstruction;
            state.data.supportStructureChanged = constructionRisks.modificationLoadBearingParts;
            state.data.ram = constructionRisks.pileDrivingElements;
            state.data.groundwater = constructionRisks.loweringGroundWater;
            state.data.underpinning = constructionRisks.undertakingBuilding;
            state.data.rock = constructionRisks.buildingOnRock;
            state.data.dangerZone = constructionRisks.constructionInDangerZone;
            state.data.constructionProject = constructionRisks.newConstruction
                ? ConstructionProjectEnum.NewConstructionOrExtension
                : ConstructionProjectEnum.ReconstructionOrRenovation;
        },
        [Successful(ConstructionStoreActions.SaveConstructionDuration)](
            state: ConstructionStoreState, constructionDuration: ConstructionDuration): void {
            state.data.duration = constructionDuration.duration;
            state.data.startDate = constructionDuration.startDate;
            state.data.completionDate = constructionDuration.completionDate;
        },
        [Successful(ConstructionStoreActions.SaveConstructionSum)](
            state: ConstructionStoreState, constructionSum: ConstructionSum): void {
            state.data.constructionSum = constructionSum.constructionSum;
        },
        [Successful(ConstructionStoreActions.SaveConstructionMatrix)](
            state: ConstructionStoreState, constructionMatrix: ConstructionMatrix): void {
            state.data.insurance = {
                ...state.data.insurance,
                ...constructionMatrix,
            };
        },
        [Successful(ConstructionStoreActions.SaveConstructionContact)](
            state: ConstructionStoreState, constructionContact: ConstructionContact): void {
            state.data.principalLiability = {
                ...state.data.principalLiability,
                salutation: constructionContact.salutation,
                postcode: constructionContact.postcode,
                canton: constructionContact.canton,
                city: constructionContact.city,
                street: constructionContact.street,
                firstName: constructionContact.firstName,
                lastName: constructionContact.lastName,
                dateOfBirth: constructionContact.dateOfBirth,
                email: constructionContact.email,
                phoneNumber: constructionContact.phone,
                companyName: constructionContact.companyName,
                languageOfCorrespondence: constructionContact.languageOfCorrespondence,
            };
        },
        [Successful(ConstructionStoreActions.SaveConstructionLanguage)](
            state: ConstructionStoreState, language: CipCodeSprache): void {
            state.data.principalLiability = {
                ...state.data.principalLiability,
                languageOfCorrespondence: language,
            };
        },
        [Successful(ConstructionStoreActions.SaveConstruction)](
            state: ConstructionStoreState, construction: Construction): void {
            const birthdate = construction.principalLiability === undefined ? undefined
                : construction.principalLiability.dateOfBirth === undefined ? undefined : construction.principalLiability.dateOfBirth;
            state.data = {
                ...state.data,
                ...construction,
                startDate: new Date(construction.startDate as any),
                completionDate: new Date(construction.completionDate as any),
                principalLiability: {
                    ...construction.principalLiability,
                    dateOfBirth: birthdate === undefined ? undefined : newDate(birthdate as any),
                },
            };
        },
        [Successful(ConstructionStoreActions.SaveQuoteId)](
            state: ConstructionStoreState, quoteId: string): void {
            state.data.quoteId = quoteId;
        },
    },
};
